import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import { estimateItemValue } from 'api/index';
import type {
  ApiInProgressType,
  EstimateItemType,
  EstimateItemValue,
  GetEstimateItemValueRequest,
  PostPatchEstimateItemValueRequest
} from 'api/types';
import addIcon from 'assets/images/add_circle.svg';
import stylusNoteIcon from 'assets/images/stylus_note.svg';
import EditableTableCell from 'components/EditableTableCell';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import CustomButton from 'components/NewLayout/Button';
import InfoBadge from 'components/NewLayout/InfoBadge';
import DataTable from 'components/NewLayout/Table';
import { useCanvasContext } from 'contexts/canvasContext';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Canvas from './Canvas';

const LabourAccordion = ({
  estimateItemId,
  estimateItemTypes,
  setTotalAmount
}: {
  estimateItemId: number;
  estimateItemTypes: EstimateItemType[];
  setTotalAmount: (val: number) => void;
}) => {
  const [apiInProgress, setApiInProgress] = useState<ApiInProgressType>({});
  const [fabricationList, setFabricationList] = useState<EstimateItemValue[]>([]);
  const [fabricationTotalAmount, setFabricationTotalAmount] = useState<number>(0);
  const [fabricationPerUnitHoursAmount, setFabricationPerUnitHoursAmount] = useState<number>(0);
  const [fabricationPerUnitDaysAmount, setFabricationialPerUnitDaysAmount] = useState<number>(0);
  const [isAddNewRowButtonDisabledFabrication, setAddNewRowButtonDisabilityFabrication] =
    useState<boolean>(false);
  const [isActionsColumnVisibleFabrication, setActionsColumnVisibilityFabrication] =
    useState<boolean>(false);
  const [labourFabricationTypeId, setLabourFabricationTypeId] = useState<number>();
  //paint
  const [paintList, setPaintList] = useState<EstimateItemValue[]>([]);
  const [paintTotalAmount, setPaintTotalAmount] = useState<number>(0);
  const [paintPerUnitHoursAmount, setPaintPerUnitHoursAmount] = useState<number>(0);
  const [paintPerUnitDaysAmount, setPaintPerUnitDaysAmount] = useState<number>(0);
  const [isActionsColumnVisiblePaint, setActionsColumnVisibilityPaint] = useState<boolean>(false);
  const [isAddNewRowButtonDisabledPaint, setAddNewRowButtonDisabilityPaint] =
    useState<boolean>(false);
  const [labourPaintTypeId, setLabourPaintTypeId] = useState<number>();
  //other labour
  const [otherLabourList, setOtherLabourList] = useState<EstimateItemValue[]>([]);
  const [otherLabourTotalAmount, setOtherLabourTotalAmount] = useState<number>(0);
  const [labourCategory, setLabourCategory] = useState<DropdownOptionType[]>([]);
  const [isActionsColumnVisibleOtherLabour, setActionsColumnVisibilityOtherLabour] =
    useState<boolean>(false);
  const [isAddNewRowButtonDisabledOtherLabour, setAddNewRowButtonDisabilityOtherLabour] =
    useState<boolean>(false);
  const { canvasVisibility, handleCanvasVisibility } = useCanvasContext();

  useEffect(() => {
    const labourCategories: DropdownOptionType[] = [];
    estimateItemTypes.forEach((item) => {
      if (item.estimate_item_type === 'Labour Fabrication') {
        setLabourFabricationTypeId(item.estimate_item_type_id);
      } else if (item.estimate_item_type === 'Labour Paint') {
        setLabourPaintTypeId(item.estimate_item_type_id);
      } else if (item.is_labour) {
        labourCategories.push({
          id: item.estimate_item_type_id,
          value: item.estimate_item_type
        });
      }
    });
    setLabourCategory(labourCategories);
  }, [estimateItemTypes]);

  useEffect(() => {
    labourFabricationTypeId && getFabricationList();
    labourPaintTypeId && getPaintList();
    labourCategory.length && getOtherLabourList();
  }, [labourFabricationTypeId, labourPaintTypeId, labourCategory]);

  useEffect(() => {
    const totals = fabricationList.reduce(
      (acc, item) => {
        if (item.estimate_item_value_id && item.estimate_item_value_id > 0) {
          const labourTotal = Number(item.labour_total);
          const labourPerUnitHours = Number(item.labour_per_unit_hours);
          const labourPerUnitDays = Number(item.labour_per_unit_days);

          acc.totalLabour += isNaN(labourTotal) ? 0 : labourTotal;
          acc.totalHours += isNaN(labourPerUnitHours) ? 0 : labourPerUnitHours;
          acc.totalDays += isNaN(labourPerUnitDays) ? 0 : labourPerUnitDays;
        }
        return acc;
      },
      { totalLabour: 0, totalHours: 0, totalDays: 0 }
    );

    setFabricationTotalAmount(totals.totalLabour);
    setFabricationPerUnitHoursAmount(totals.totalHours);
    setFabricationialPerUnitDaysAmount(totals.totalDays);
    setTotalAmount(totals.totalLabour + paintTotalAmount + otherLabourTotalAmount);
  }, [fabricationList]);

  useEffect(() => {
    const totals = paintList.reduce(
      (acc, item) => {
        if (item.estimate_item_value_id && item.estimate_item_value_id > 0) {
          const labourTotal = Number(item.labour_total);
          const labourPerUnitHours = Number(item.labour_per_unit_hours);
          const labourPerUnitDays = Number(item.labour_per_unit_days);

          acc.totalLabour += isNaN(labourTotal) ? 0 : labourTotal;
          acc.totalHours += isNaN(labourPerUnitHours) ? 0 : labourPerUnitHours;
          acc.totalDays += isNaN(labourPerUnitDays) ? 0 : labourPerUnitDays;
        }
        return acc;
      },
      { totalLabour: 0, totalHours: 0, totalDays: 0 }
    );

    setPaintTotalAmount(totals.totalLabour);
    setPaintPerUnitHoursAmount(totals.totalHours);
    setPaintPerUnitDaysAmount(totals.totalDays);
    setTotalAmount(totals.totalLabour + fabricationTotalAmount + otherLabourTotalAmount);
  }, [paintList]);

  useEffect(() => {
    const totals = otherLabourList.reduce(
      (acc, item) => {
        if (item.estimate_item_value_id && item.estimate_item_value_id > 0) {
          const labourTotal = Number(item.labour_total);
          acc.totalLabour += isNaN(labourTotal) ? 0 : labourTotal;
        }
        return acc;
      },
      { totalLabour: 0 }
    );
    setOtherLabourTotalAmount(totals.totalLabour);
    setTotalAmount(totals.totalLabour + fabricationTotalAmount + paintTotalAmount);
  }, [otherLabourList]);

  const updateEstimateItemValue = async (
    id: number,
    fieldName: string,
    val: string | boolean,
    currentList: EstimateItemValue[]
  ) => {
    try {
      const item = currentList.find((item) => item.estimate_item_value_id === id);

      if (item) {
        const labour_per_unit_hours_calculation =
          Number(item.labour_quantity) * Number(item.labour_hours_per);

        const dataToUpdate: PostPatchEstimateItemValueRequest = {
          http_method: 'PATCH',
          data: {
            estimate_item_value_id: id,
            [fieldName]: val,
            labour_per_unit_hours: labour_per_unit_hours_calculation.toFixed(2),
            labour_per_unit_days: (labour_per_unit_hours_calculation / 8).toFixed(2),
            labour_total: (Number(item.labour_quantity) * Number(item.labour_hours_per)).toFixed(2)
          }
        };

        await estimateItemValue(dataToUpdate);
        toast.success('Item updated successfully!', {
          position: 'top-center',
          id: 'update_success'
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Error updating item.', {
        position: 'top-center',
        id: 'update_failure'
      });
    }
  };

  const updateOtherLabor = async (
    id: number,
    fieldName: string,
    val: string | boolean,
    currentList: EstimateItemValue[]
  ) => {
    try {
      const item = currentList.find((item) => item.estimate_item_value_id === id);

      if (item) {
        const dataToUpdate: PostPatchEstimateItemValueRequest = {
          http_method: 'PATCH',
          data: {
            estimate_item_value_id: id,
            [fieldName]: val,
            labour_total: (Number(item.labour_quantity) * Number(item.labour_hours_per)).toFixed(2)
          }
        };

        await estimateItemValue(dataToUpdate);
        toast.success('Item updated successfully!', {
          position: 'top-center',
          id: 'update_success'
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Error updating item.', {
        position: 'top-center',
        id: 'update_failure'
      });
    }
  };

  const onEditingComplete = (
    fieldName: string,
    val: boolean | string,
    id: number,
    cellId: string,
    table: 'Fabrication' | 'Paint' | 'Other Labour'
  ) => {
    if (!val || val.toString().trim() === '') {
      toast.error('Field is required!', { position: 'top-center', id: 'required_field' });
      return;
    }

    const updateListWithCalculations = (list: EstimateItemValue[]) =>
      list.map((row) => {
        if (row.estimate_item_value_id !== id) return row;

        const updatedRow = { ...row, [fieldName]: val };
        const labourQuantity = Number(updatedRow.labour_quantity);
        const labourHoursPer = Number(updatedRow.labour_hours_per);

        if (!isNaN(labourQuantity) && !isNaN(labourHoursPer)) {
          const labour_per_unit_hours_calculation = labourQuantity * labourHoursPer;
          updatedRow.labour_total = (labourQuantity * labourHoursPer).toFixed(2);
          updatedRow.labour_per_unit_hours = labour_per_unit_hours_calculation.toFixed(2);
          updatedRow.labour_per_unit_days = (labour_per_unit_hours_calculation / 8).toFixed(2);
        }

        return updatedRow;
      });

    const updateTable = () => {
      switch (table) {
        case 'Fabrication': {
          const fabricationUpdated = updateListWithCalculations(fabricationList);
          setFabricationList(fabricationUpdated);
          id > 0 && updateEstimateItemValue(id, fieldName, val, fabricationUpdated);
          break;
        }
        case 'Paint': {
          const paintUpdated = updateListWithCalculations(paintList);
          setPaintList(paintUpdated);
          id > 0 && updateEstimateItemValue(id, fieldName, val, paintUpdated);
          break;
        }
        case 'Other Labour': {
          const otherLabourUpdated = updateListWithCalculations(otherLabourList);
          setOtherLabourList(otherLabourUpdated);
          id > 0 && updateOtherLabor(id, fieldName, val, otherLabourUpdated);
          break;
        }
      }
    };

    updateTable();
    setApiInProgress((prev) => ({ ...prev, [cellId]: false }));
  };

  const fabricationColumns = [
    isActionsColumnVisibleFabrication && {
      name: 'Actions',
      prop: 'actions',
      data: (row) => {
        if (row.estimate_item_value_id > 0) return null;
        return (
          <>
            <IconButton color="primary" onClick={() => handleDeleteRowFabrication()}>
              <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton color="success" onClick={() => handleApproveRowFabrication()}>
              <CheckIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </>
        );
      }
    },
    {
      name: 'Material Name',
      prop: 'labour_item_name',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-fabrication`;
        return (
          <EditableTableCell
            name="labour_item_name"
            value={row.labour_item_name || ''}
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_item_name',
                val,
                row.estimate_item_value_id,
                cellId,
                'Fabrication'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Quantity',
      prop: 'labour_quantity',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-fabrication`;
        return (
          <EditableTableCell
            name="labour_quantity"
            value={row.labour_quantity || ''}
            type="number"
            min="0"
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_quantity',
                val,
                row.estimate_item_value_id,
                cellId,
                'Fabrication'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Hours Per',
      prop: 'labour_hours_per',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-fabrication`;
        return (
          <EditableTableCell
            name="labour_hours_per"
            value={row.labour_hours_per ? `${row.labour_hours_per}` : ''}
            type="text"
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_hours_per',
                val,
                row.estimate_item_value_id,
                cellId,
                'Fabrication'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Per Unit Hours',
      prop: 'labour_per_unit_hours',
      data: (row) => `${row.labour_per_unit_hours || '0.00'}`
    },
    {
      name: 'Per Unit Days',
      prop: 'labour_per_unit_days',
      data: (row) => `${row.labour_per_unit_days || '0.00'}`
    },
    {
      name: 'Total',
      prop: 'labour_total',
      data: (row) => `${row.labour_total || '0.00'}`
    }
  ];

  const addNewRowFabrication = () => {
    setFabricationList((prevList) => [
      ...prevList,
      {
        estimate_item_value_id: 0,
        estimate_item_id: estimateItemId,
        estimate_item_type_id: labourFabricationTypeId,
        labour_item_name: '',
        labour_quantity: 0,
        labour_labour_hours_per: 0,
        labour_labour_per_unit_hours: 0,
        labour_labour_per_unit_days: 0
      }
    ]);
    setActionsColumnVisibilityFabrication(true);
    setAddNewRowButtonDisabilityFabrication(true);
  };

  const handleDeleteRowFabrication = () => {
    setFabricationList((currentList) => {
      const updatedList = [...currentList];
      updatedList.pop();
      return updatedList;
    });
    setAddNewRowButtonDisabilityFabrication(false);
    setActionsColumnVisibilityFabrication(false);
  };

  const handleApproveRowFabrication = async () => {
    try {
      const lastFabricationItem = fabricationList[fabricationList.length - 1];

      const { estimate_item_type_id, labour_item_name, labour_quantity, labour_hours_per } =
        lastFabricationItem;

      if (!labour_item_name || !labour_quantity || !labour_hours_per) {
        toast.error('All fields must be filled to submit the row!', {
          position: 'top-center',
          id: 'required_fields'
        });
        return;
      }

      const labour_per_unit_hours_calculation = Number(labour_quantity) * Number(labour_hours_per);

      const dataToPost: PostPatchEstimateItemValueRequest = {
        http_method: 'POST',
        data: {
          estimate_item_id: estimateItemId,
          estimate_item_type_id,
          labour_item_name,
          labour_quantity,
          labour_hours_per,
          labour_per_unit_hours: labour_per_unit_hours_calculation.toFixed(2),
          labour_per_unit_days: (labour_per_unit_hours_calculation / 8).toFixed(2),
          labour_total: (Number(labour_quantity) * Number(labour_hours_per)).toFixed(2)
        }
      };

      await estimateItemValue(dataToPost);
      toast.success('Item added successfully!', { position: 'top-center', id: 'create_success' });
      await getFabricationList();
      setActionsColumnVisibilityFabrication(false);
      setAddNewRowButtonDisabilityFabrication(false);
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item. Please try again.', {
        position: 'top-center',
        id: 'create_failure'
      });
    }
  };

  const paintColumns = [
    isActionsColumnVisiblePaint && {
      name: 'Actions',
      prop: 'actions',
      data: (row) => {
        if (row.estimate_item_value_id > 0) return null;
        return (
          <>
            <IconButton color="primary" onClick={() => handleDeleteRowPaint()}>
              <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton color="success" onClick={() => handleApproveRowPaint()}>
              <CheckIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </>
        );
      }
    },
    {
      name: 'Material Name',
      prop: 'labour_item_name',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-paint`;
        return (
          <EditableTableCell
            name="labour_item_name"
            value={row.labour_item_name || ''}
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_item_name',
                val,
                row.estimate_item_value_id,
                cellId,
                'Paint'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Quantity',
      prop: 'labour_quantity',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-paint`;
        return (
          <EditableTableCell
            name="labour_quantity"
            value={row.labour_quantity || ''}
            type="number"
            onEditComplete={(val) =>
              onEditingComplete('labour_quantity', val, row.estimate_item_value_id, cellId, 'Paint')
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Hours Per',
      prop: 'labour_hours_per',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-paint`;
        return (
          <EditableTableCell
            name="labour_hours_per"
            value={row.labour_hours_per ? `${row.labour_hours_per}` : ''}
            type="text"
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_hours_per',
                val,
                row.estimate_item_value_id,
                cellId,
                'Paint'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Per Unit Hours',
      prop: 'labour_per_unit_hours',
      data: (row) => `${row.labour_per_unit_hours || '0.00'}`
    },
    {
      name: 'Per Unit Days',
      prop: 'labour_per_unit_days',
      data: (row) => `${row.labour_per_unit_days || '0.00'}`
    },
    {
      name: 'Total',
      prop: 'labour_total',
      data: (row) => `${row.labour_total || '0.00'}`
    }
  ];

  const addNewRowPaint = () => {
    setPaintList((prevList) => [
      ...prevList,
      {
        estimate_item_value_id: 0,
        estimate_item_id: estimateItemId,
        estimate_item_type_id: labourPaintTypeId,
        labour_item_name: '',
        labour_quantity: 0,
        labour_labour_hours_per: 0,
        labour_labour_per_unit_hours: 0,
        labour_labour_per_unit_days: 0
      }
    ]);
    setActionsColumnVisibilityPaint(true);
    setAddNewRowButtonDisabilityPaint(true);
  };

  const handleDeleteRowPaint = () => {
    setPaintList((currentList) => {
      const updatedList = [...currentList];
      updatedList.pop();
      return updatedList;
    });
    setAddNewRowButtonDisabilityPaint(false);
    setActionsColumnVisibilityPaint(false);
  };

  const handleApproveRowPaint = async () => {
    try {
      const lastPaintItem = paintList[paintList.length - 1];

      const { estimate_item_type_id, labour_item_name, labour_quantity, labour_hours_per } =
        lastPaintItem;

      if (!labour_item_name || !labour_quantity || !labour_hours_per) {
        toast.error('All fields must be filled to submit the row!', {
          position: 'top-center',
          id: 'required_field'
        });
        return;
      }

      const labour_per_unit_hours_calculation = Number(labour_quantity) * Number(labour_hours_per);

      const dataToPost: PostPatchEstimateItemValueRequest = {
        http_method: 'POST',
        data: {
          estimate_item_id: estimateItemId,
          estimate_item_type_id,
          labour_item_name,
          labour_quantity,
          labour_hours_per,
          labour_per_unit_hours: labour_per_unit_hours_calculation.toFixed(2),
          labour_per_unit_days: (labour_per_unit_hours_calculation / 8).toFixed(2),
          labour_total: (Number(labour_quantity) * Number(labour_hours_per)).toFixed(2)
        }
      };

      await estimateItemValue(dataToPost);
      toast.success('Item added successfully!', { position: 'top-center', id: 'create_success' });
      await getPaintList();
      setActionsColumnVisibilityPaint(false);
      setAddNewRowButtonDisabilityPaint(false);
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item. Please try again.', {
        position: 'top-center',
        id: 'create_failure'
      });
    }
  };

  const otherLabourColumns = [
    isActionsColumnVisibleOtherLabour && {
      name: 'Actions',
      prop: 'actions',
      data: (row) => {
        if (row.estimate_item_value_id > 0) return null;
        return (
          <>
            <IconButton color="primary" onClick={() => handleDeleteRowOtherLabour()}>
              <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton color="success" onClick={() => handleApproveRowOtherLabour()}>
              <CheckIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </>
        );
      }
    },
    {
      name: 'Other Labor',
      prop: 'estimate_item_type_id',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-other-labour`;
        return (
          <EditableTableCell
            name="estimate_item_type_id"
            value={
              row.estimate_item_type_id
                ? labourCategory.find((v) => v.id == row.estimate_item_type_id)?.value || ''
                : 'Choose One'
            }
            onEditComplete={(val) => {
              val != 'Choose One'
                ? onEditingComplete(
                    'estimate_item_type_id',
                    val,
                    row.estimate_item_value_id,
                    cellId,
                    'Other Labour'
                  )
                : '';
            }}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            isDropdown
            options={labourCategory}
            key={cellId}
          />
        );
      }
    },
    {
      name: 'Quantity',
      prop: 'labour_quantity',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-other-labour`;
        return (
          <EditableTableCell
            name="labour_quantity"
            value={row.labour_quantity || ''}
            type="number"
            min="0"
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_quantity',
                val,
                row.estimate_item_value_id,
                cellId,
                'Other Labour'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Hours Per',
      prop: 'labour_hours_per',
      data: (row, index) => {
        const cellId = `${row.estimate_item_value_id}-${index}-other-labour`;
        return (
          <EditableTableCell
            name="labour_hours_per"
            value={row.labour_hours_per ? `${row.labour_hours_per}` : ''}
            type="text"
            onEditComplete={(val) =>
              onEditingComplete(
                'labour_hours_per',
                val,
                row.estimate_item_value_id,
                cellId,
                'Other Labour'
              )
            }
            key={cellId}
            apiInProgress={apiInProgress[cellId]}
            setApiInProgress={(val) =>
              setApiInProgress((prev) => ({
                ...prev,
                [cellId]: val
              }))
            }
            selectText
          />
        );
      }
    },
    {
      name: 'Total',
      prop: 'labour_total',
      data: (row) => `${row.labour_total || '0.00'}`
    }
  ];

  const addNewRowOtherLabour = () => {
    setOtherLabourList((prevList) => [
      ...prevList,
      {
        estimate_item_value_id: 0,
        estimate_item_id: estimateItemId,
        estimate_item_type_id: 0,
        labour_quantity: 0,
        labour_labour_hours_per: 0
      }
    ]);
    setActionsColumnVisibilityOtherLabour(true);
    setAddNewRowButtonDisabilityOtherLabour(true);
  };

  const handleDeleteRowOtherLabour = () => {
    setOtherLabourList((currentList) => {
      const updatedList = [...currentList];
      updatedList.pop();
      return updatedList;
    });
    setAddNewRowButtonDisabilityOtherLabour(false);
    setActionsColumnVisibilityOtherLabour(false);
  };

  const handleApproveRowOtherLabour = async () => {
    try {
      const lastOtherLabourItem = otherLabourList[otherLabourList.length - 1];

      const { estimate_item_type_id, labour_quantity, labour_hours_per } = lastOtherLabourItem;

      if (!labour_quantity || !labour_hours_per || !estimate_item_type_id) {
        toast.error('All fields must be filled to submit the row!', {
          position: 'top-center',
          id: 'required_fields'
        });
        return;
      }

      const dataToPost: PostPatchEstimateItemValueRequest = {
        http_method: 'POST',
        data: {
          estimate_item_id: estimateItemId,
          estimate_item_type_id,
          labour_quantity,
          labour_hours_per,
          labour_total: (Number(labour_quantity) * Number(labour_hours_per)).toFixed(2)
        }
      };

      await estimateItemValue(dataToPost);
      toast.success('Item added successfully!', { position: 'top-center', id: 'create_success' });
      await getOtherLabourList();
      setActionsColumnVisibilityOtherLabour(false);
      setAddNewRowButtonDisabilityOtherLabour(false);
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item. Please try again.', {
        position: 'top-center',
        id: 'create_failure'
      });
    }
  };

  const getFabricationList = async () => {
    const getData: GetEstimateItemValueRequest = {
      http_method: 'GET',
      data: {
        estimateItemId,
        estimateItemTypeId: labourFabricationTypeId
      }
    };
    const getEstimateItemValue = await estimateItemValue(getData);
    setFabricationList(getEstimateItemValue);
  };

  const getPaintList = async () => {
    const getData: GetEstimateItemValueRequest = {
      http_method: 'GET',
      data: {
        estimateItemId,
        estimateItemTypeId: labourPaintTypeId
      }
    };
    const getEstimateItemValue = await estimateItemValue(getData);
    setPaintList(getEstimateItemValue);
  };

  const getOtherLabourList = async () => {
    const getData: GetEstimateItemValueRequest = {
      http_method: 'GET',
      data: {
        estimateItemId,
        isLabour: true
      }
    };
    const getEstimateItemValue = await estimateItemValue(getData);
    setOtherLabourList(getEstimateItemValue);
  };

  return (
    <Accordion className="mui-accordion">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '0px'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: '500',
                  fontSize: '22px',
                  lineHeight: '30px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none'
                }}>
                Labor
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: '1px solid #000' }}>
        {/* 1st section  */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginY: '10px' }}>
            <Box>
              <Typography sx={{ fontSize: '26px' }}>Fabrication</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <InfoBadge
                label="Fabrication Total"
                badgeValue={`${fabricationTotalAmount.toFixed(2)}`}
              />
              <InfoBadge
                label="Per Unit Hours"
                badgeValue={`${fabricationPerUnitHoursAmount.toFixed(2)}`}
              />
              <InfoBadge
                label="Per Unit Days"
                badgeValue={`${fabricationPerUnitDaysAmount.toFixed(2)}`}
              />
              <CustomButton
                disabled={isAddNewRowButtonDisabledFabrication}
                sx={{ marginLeft: '5px' }}
                onClick={() => addNewRowFabrication()}
                label={
                  <>
                    <img src={addIcon} style={{ marginRight: '5px' }} /> Add New Row
                  </>
                }
              />
            </Box>
          </Box>
          <Box>
            <DataTable items={fabricationList} columns={fabricationColumns} totalCount={0} />
          </Box>
        </Box>

        {/* 2nd section  */}
        {canvasVisibility.labourFabrication ? (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Canvas estimateItemId={estimateItemId} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginY: '10px'
            }}>
            <CustomButton
              sx={{
                border: '1px dashed #335d87',
                backgroundColor: '#fff',
                color: '#335d87',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { backgroundColor: '#fff' }
              }}
              onClick={() => handleCanvasVisibility('labourFabrication')}
              label={
                <>
                  <img src={stylusNoteIcon} style={{ marginRight: '5px' }} />
                  <span>Move Canvas Here</span>
                </>
              }
            />
          </Box>
        )}

        {/* 3rd section  */}
        <Box sx={{ marginTop: '40px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginY: '10px' }}>
            <Box>
              <Typography sx={{ fontSize: '26px' }}>Paint</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <InfoBadge label="Paint Total" badgeValue={`${paintTotalAmount.toFixed(2)}`} />
              <InfoBadge
                label="Per Unit Hours"
                badgeValue={`${paintPerUnitHoursAmount.toFixed(2)}`}
              />
              <InfoBadge
                label="Per Unit Days"
                badgeValue={`${paintPerUnitDaysAmount.toFixed(2)}`}
              />
              <CustomButton
                disabled={isAddNewRowButtonDisabledPaint}
                sx={{ marginLeft: '5px' }}
                onClick={() => addNewRowPaint()}
                label={
                  <>
                    <img src={addIcon} style={{ marginRight: '5px' }} /> Add New Row
                  </>
                }
              />
            </Box>
          </Box>
          <Box>
            <DataTable items={paintList} columns={paintColumns} totalCount={0} />
          </Box>
        </Box>

        {/* 4th section  */}
        {canvasVisibility.labourPaint ? (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Canvas estimateItemId={estimateItemId} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginY: '10px'
            }}>
            <CustomButton
              sx={{
                border: '1px dashed #335d87',
                backgroundColor: '#fff',
                color: '#335d87',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { backgroundColor: '#fff' }
              }}
              onClick={() => handleCanvasVisibility('labourPaint')}
              label={
                <>
                  <img src={stylusNoteIcon} style={{ marginRight: '5px' }} />
                  <span>Move Canvas Here</span>
                </>
              }
            />
          </Box>
        )}

        {/* 5th section  */}
        <Box sx={{ marginTop: '40px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginY: '10px' }}>
            <Box>
              <Typography sx={{ fontSize: '26px' }}>Other Labor</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <CustomButton
                disabled={isAddNewRowButtonDisabledOtherLabour}
                sx={{ marginLeft: '5px' }}
                onClick={() => addNewRowOtherLabour()}
                label={
                  <>
                    <img src={addIcon} style={{ marginRight: '5px' }} /> Add New Row
                  </>
                }
              />
            </Box>
          </Box>
          <Box>
            <DataTable items={otherLabourList} columns={otherLabourColumns} totalCount={0} />
          </Box>
        </Box>

        {/* 6th section  */}
        {canvasVisibility.labourOther ? (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Canvas estimateItemId={estimateItemId} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginY: '10px'
            }}>
            <CustomButton
              sx={{
                border: '1px dashed #335d87',
                backgroundColor: '#fff',
                color: '#335d87',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { backgroundColor: '#fff' }
              }}
              onClick={() => handleCanvasVisibility('labourOther')}
              label={
                <>
                  <img src={stylusNoteIcon} style={{ marginRight: '5px' }} />
                  <span>Move Canvas Here</span>
                </>
              }
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default LabourAccordion;
